import React, { useEffect, useRef, useState } from 'react';

import { InView } from 'react-intersection-observer';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';
import { formatCurrency } from '@atc/string-fns';

import {
    IconButton,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalScrollPanel,
    ModalTitle,
    Tab,
    Tabs,
    Text,
} from 'reaxl';
import { VideoPlaceholder } from 'reaxl-video-player';

function ListingDescription({
    modalTitle,
    pricingDetail,
}) {
    const { incentive, salePrice, msrp } = pricingDetail;
    const price = incentive || salePrice || msrp || '';

    return (
        <>
            {modalTitle}
            {!!price && <span className="text-normal">{` - ${formatCurrency(price)}`}</span>}
        </>
    );
}

function MediaGalleryModalContent({
    mediaDataSet,
    tabTitle,
    mediaContentClassName,
    scrollToIndex,
    show,
    ...rest
}) {
    const itemRefs = useRef([]);

    useEffect(() => {
        if (show && tabTitle === 'Photos' && typeof scrollToIndex === 'number') {
            itemRefs.current[scrollToIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    // To avoid unnecessary re-renders, it only scrolls once when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderMediaItems = mediaDataSet.map(({ element, isUseInView }, mediaIndex) => (
        tabTitle === 'Video' && isUseInView ? (
            <InView
                key={`${tabTitle}-${mediaIndex}`}
                triggerOnce
            >
                {({ ref, inView }) => (
                    <div
                        ref={ref}
                    >
                        {inView ? element : (
                            <VideoPlaceholder
                                height={375}
                                width={500}
                            />
                        )}
                    </div>
                )}
            </InView>
        ) : (
            <div
                key={`${tabTitle}-${mediaIndex}`}
                ref={tabTitle === 'Photos' ? ((el) => { itemRefs.current[mediaIndex] = el; }) : undefined}
            >
                {element}
            </div>
        )
    ));

    const mediaContentClass = clsx(
        'media-content-wrapper position-absolute w-100 top-0 left-0 display-grid gap-1',
        mediaContentClassName
    );

    return (
        <ModalScrollPanel
            tabIndex="0"
            {...rest}
        >
            <div className={mediaContentClass}>
                {renderMediaItems}
            </div>
        </ModalScrollPanel>
    );
}

function MediaGalleryModal({
    defaultActiveKey,
    headerCtaContent,
    hideMediaGaleryModal,
    isMobile,
    modalTitle,
    loadSpin,
    onTabClick,
    overlayMediaSet,
    pricingDetail,
    relocateCTAs,
    show,
    showFullScreen,
    disableTabs,
    scrollToIndex,
}) {

    const {
        media_gallery: [, { inverse: isInverse }],
        media_slider: [isMediaSliderEnabled],
    } = useFeatures([
        'media_gallery',
        'media_slider',
    ]);

    const [activeKey, setActiveKey] = useState(defaultActiveKey);

    const handleTabSelect = (selectedKey) => {
        setActiveKey(selectedKey);
        loadSpin(selectedKey);
        onTabClick(overlayMediaSet[selectedKey].tabTitle);
    };

    const wrappedHeaderCTAs = !isMobile && headerCtaContent ? (
        <React.Fragment key="header-cta-wrapper">
            {headerCtaContent}
        </React.Fragment>
    ) : undefined;

    return (
        <Modal
            show={show}
            onHide={() => {
                hideMediaGaleryModal();
            }}
            bsSize="large"
            data-cmp="mediaGalleryModal"
            backdropStyle={{
                zIndex: 20,
            }}
            keyboard={!showFullScreen}
            enforceFocus={!showFullScreen}
            className={clsx({ inverted: isInverse && !isMediaSliderEnabled })}
        >
            <ModalHeader
                closeButton={!isMobile}
                className="border-bottom-0"
            >
                <ModalTitle
                    className={clsx({
                        'display-flex w-100 justify-content-between': isMobile && !relocateCTAs,
                    })}
                >
                    {isMobile && (
                        <IconButton
                            label="Close"
                            glyph="chevron-left"
                            monochrome
                            hideLabel
                            onClick={hideMediaGaleryModal}
                        />
                    )}
                    {isMobile && !relocateCTAs && headerCtaContent}
                    {(relocateCTAs || !isMobile) && (
                        <ListingDescription
                            pricingDetail={pricingDetail}
                            modalTitle={modalTitle}
                        />
                    )}
                </ModalTitle>
            </ModalHeader>
            <ModalBody>
                {isMobile && !relocateCTAs && (
                    <Text
                        weight="bold"
                        className="margin-bottom-1"
                    >
                        <ListingDescription
                            pricingDetail={pricingDetail}
                            modalTitle={modalTitle}
                        />
                    </Text>
                )}
                {disableTabs ? (
                    <MediaGalleryModalContent
                        tabTitle={overlayMediaSet[0].tabTitle}
                        mediaDataSet={overlayMediaSet[0].mediaDataSet}
                        className="h-100 margin-top-0 margin-top-sm-5"
                        style={{ maxHeight: 'unset', position: 'relative', scrollbarWidth: 'none' }}
                        mediaContentClassName="position-absolute"
                    />
                ) : (
                    <Tabs
                        activeKey={activeKey}
                        additionalContentTab={wrappedHeaderCTAs}
                        alignment="left"
                        onSelect={handleTabSelect}
                        uiContext={isMediaSliderEnabled || !isInverse ? 'light' : 'dark'}
                        enableSliderAnimation
                        className="h-100 display-flex flex-column"
                    >
                        {overlayMediaSet.map(({ tabTitle, mediaDataSet }, mediaDatasetIndex) => (
                            <Tab
                                key={`media-tab-${tabTitle}`}
                                eventKey={mediaDatasetIndex}
                                title={tabTitle}
                            >
                                <MediaGalleryModalContent
                                    mediaDataSet={mediaDataSet}
                                    tabTitle={tabTitle}
                                    scrollToIndex={scrollToIndex}
                                    show={show}
                                />
                            </Tab>
                        ))}
                    </Tabs>
                )}
            </ModalBody>
            {relocateCTAs && (
                <ModalFooter>
                    {headerCtaContent}
                </ModalFooter>
            )}
        </Modal>
    );
}

MediaGalleryModal.propTypes = {
    activeKey: PropTypes.number,
    defaultActiveKey: PropTypes.number,
    headerCtaContent: PropTypes.element,
    hideMediaGaleryModal: PropTypes.func,
    isMobile: PropTypes.bool,
    modalTitle: PropTypes.string,
    loadSpin: PropTypes.func,
    onTabClick: PropTypes.func,
    overlayMediaSet: PropTypes.array,
    pricingDetail: PropTypes.object,
    show: PropTypes.bool,
    showFullScreen: PropTypes.bool,
    disableTabs: PropTypes.bool,
    scrollToIndex: PropTypes.number,
};

MediaGalleryModal.defaultProps = {
    activeKey: null,
    defaultActiveKey: 0,
    headerCtaContent: null,
    hideMediaGaleryModal: () => { },
    isMobile: false,
    modalTitle: '',
    loadSpin: () => { },
    onTabClick: () => { },
    overlayMediaSet: [],
    pricingDetail: {},
    show: false,
    disableTabs: false,
    scrollToIndex: 0,
};

export default MediaGalleryModal;
